import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Users,
  ScrollText,
  ArrowRight,
} from "lucide-react";

export default function TeacherOverview() {
  const { user } = useAuth();
  const [gradebook, setGradebook] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teacher/gradebook");
        setGradebook(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalStudents = gradebook.reduce((s, c) => s + c.students.length, 0);
  const avgProgress = (() => {
    const all = gradebook.flatMap((c) => c.students.map((s) => s.progress_pct || 0));
    return all.length === 0 ? 0 : Math.round(all.reduce((a, b) => a + b, 0) / all.length);
  })();

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Teacher · Console
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
          Welcome, {user?.name?.split(" ")[1] || user?.name}
        </h1>
      </div>

      <div className="mb-10 grid grid-cols-1 gap-px bg-foreground sm:grid-cols-3">
        <Kpi icon={<BookOpen className="h-5 w-5" />} label="Courses" value={gradebook.length} />
        <Kpi icon={<Users className="h-5 w-5" />} label="Students Enrolled" value={totalStudents} />
        <Kpi icon={<ScrollText className="h-5 w-5" />} label="Avg Progress" value={`${avgProgress}%`} />
      </div>

      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /your courses
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">
            Cohorts at a glance
          </h2>
        </div>
        <Link to="/app/teacher/gradebook" className="mono text-[11px] uppercase tracking-widest hover:underline">
          Open gradebook →
        </Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-32 animate-pulse border border-border bg-muted" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {gradebook.map((c) => {
            const avg =
              c.students.length === 0
                ? 0
                : Math.round(
                    c.students.reduce((s, x) => s + (x.progress_pct || 0), 0) /
                      c.students.length
                  );
            return (
              <div key={c.course_id} className="border-2 border-foreground bg-background p-5 hover-lift" data-testid={`teacher-course-${c.course_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {c.category}
                    </div>
                    <div className="font-display mt-1 text-lg font-extrabold tracking-tight">
                      {c.course_title}
                    </div>
                  </div>
                  <Link
                    to="/app/teacher/gradebook"
                    className="flex h-9 w-9 items-center justify-center bg-foreground text-background"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4 border-t border-border pt-4">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Enrolled
                    </div>
                    <div className="font-display text-2xl font-extrabold">
                      {c.students.length}
                    </div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Avg progress
                    </div>
                    <div className="font-display text-2xl font-extrabold">{avg}%</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Kpi({ icon, label, value }) {
  return (
    <div className="bg-background p-6">
      <div className="flex h-9 w-9 items-center justify-center bg-foreground text-background">
        {icon}
      </div>
      <div className="mt-5 font-display text-3xl font-extrabold tracking-tight">{value}</div>
      <div className="mono mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
